"use strict";
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Input = exports.Message = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const PasswordHide_1 = require("@kawaui/icons/build/icons/action/PasswordHide");
const PasswordSee_1 = require("@kawaui/icons/build/icons/action/PasswordSee");
const Error_1 = require("@kawaui/icons/build/icons/informative/Error");
const ArrowDown_1 = require("@kawaui/icons/build/icons/navigation/ArrowDown");
const ArrowUp_1 = require("@kawaui/icons/build/icons/navigation/ArrowUp");
const react_1 = require("react");
const Input_styles_1 = require("./Input.styles");
const isPasswordType = (type) => type === 'password';
const isNumericType = (type) => type === 'number';
const getPasswordType = (hidden) => (hidden ? 'password' : 'text');
const PasswordToggle = ({ onClick, hidden }) => ((0, jsx_runtime_1.jsx)(Input_styles_1.StyledPasswordToggle, { onClick: onClick, children: hidden ? ((0, jsx_runtime_1.jsx)(PasswordHide_1.IconPasswordHide, { "aria-label": "view password", width: "24px", height: "24px" })) : ((0, jsx_runtime_1.jsx)(PasswordSee_1.IconPasswordSee, { "aria-label": "hide password", width: "24px", height: "24px" })) }));
const Arrows = ({ value, max, min, increment, decrement, disabled }) => ((0, jsx_runtime_1.jsxs)(Input_styles_1.StyledArrows, { children: [(0, jsx_runtime_1.jsx)("button", { onClick: increment, disabled: disabled, children: (0, jsx_runtime_1.jsx)(ArrowUp_1.IconArrowUp, { width: "16px", height: "16px", "aria-disabled": value === max }) }), (0, jsx_runtime_1.jsx)("button", { onClick: decrement, disabled: disabled, children: (0, jsx_runtime_1.jsx)(ArrowDown_1.IconArrowDown, { width: "16px", height: "16px", "aria-disabled": value === min }) })] }));
const Message = ({ children, isError }) => ((0, jsx_runtime_1.jsx)(Input_styles_1.StyledMessage, { isError: isError, children: children }));
exports.Message = Message;
const getMaskedValue = (value, regExpMask) => {
    const regexp = new RegExp(regExpMask, 'g');
    const matchValue = value.replace(/\s+/g, '').match(regexp);
    if (matchValue) {
        return matchValue.join(' ');
    }
    return value;
};
const getNewCaretPosition = (caret, maskedValue, isBackspacePressed) => {
    const nextValue = maskedValue[caret - 1];
    let extraSpace = nextValue === ' ' ? 1 : 0;
    if (isBackspacePressed)
        extraSpace = 0;
    const caretPos = caret + extraSpace;
    return caretPos;
};
exports.Input = (0, react_1.forwardRef)((props, ref) => {
    const { className, clearable, errorMessage, hasArrow, hasError, helperText, isAbsoluteNumber, isFullWidth, label, max, min, onChange, regExpMask, startIcon } = props, otherProps = __rest(props, ["className", "clearable", "errorMessage", "hasArrow", "hasError", "helperText", "isAbsoluteNumber", "isFullWidth", "label", "max", "min", "onChange", "regExpMask", "startIcon"]);
    const [hidden, setHidden] = (0, react_1.useState)(isPasswordType(props.type));
    const [value, setValue] = (0, react_1.useState)(props.value);
    const [type, setType] = (0, react_1.useState)(props.type);
    const [prevCaret, setPrevCaret] = (0, react_1.useState)(0);
    const [isBackspacePressed, setIsBackspacePressed] = (0, react_1.useState)(false);
    const handleKeyDown = (event) => {
        setIsBackspacePressed(event.key === 'Backspace');
    };
    const isNumeric = isNumericType(props.type);
    const inputRef = (0, react_1.useRef)(null);
    const handleChange = (0, react_1.useCallback)((event) => {
        if (!regExpMask) {
            setValue(event.target.value);
        }
        else {
            event.persist();
            const caret = event.target.selectionStart || 0;
            setPrevCaret(caret);
            const maskedValue = getMaskedValue(event.target.value, regExpMask);
            setValue(maskedValue);
            const caretPos = getNewCaretPosition(caret, maskedValue, isBackspacePressed);
            window.requestAnimationFrame(() => {
                event.target.setSelectionRange(caretPos, caretPos);
            });
        }
        if (onChange)
            onChange(event);
    }, [onChange, isBackspacePressed, regExpMask]);
    const updateValue = (newValue) => {
        var _a;
        if (!inputRef.current)
            return;
        const setInputValue = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value');
        const event = new Event('change', { bubbles: true });
        (_a = setInputValue === null || setInputValue === void 0 ? void 0 : setInputValue.set) === null || _a === void 0 ? void 0 : _a.call(inputRef.current, newValue);
        inputRef.current.dispatchEvent(event);
    };
    const increment = () => {
        if (!inputRef.current)
            return;
        const newValue = Number(inputRef.current.value) + 1;
        if (newValue > Number(max))
            return;
        updateValue(newValue.toString());
    };
    const decrement = () => {
        if (!inputRef.current)
            return;
        const newValue = Number(inputRef.current.value) - 1;
        if (newValue < Number(min))
            return;
        updateValue(newValue.toString());
    };
    const handleClear = () => {
        var _a;
        updateValue('');
        (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    };
    const togglePassword = (0, react_1.useCallback)(() => {
        setHidden(!hidden);
    }, [hidden]);
    (0, react_1.useEffect)(() => {
        if (typeof props.value !== 'undefined') {
            if (regExpMask && typeof ref !== 'function' && typeof props.value === 'string') {
                const maskedValue = getMaskedValue(props.value, regExpMask);
                setValue(maskedValue);
                const caretPos = getNewCaretPosition(prevCaret, maskedValue, isBackspacePressed);
                window.requestAnimationFrame(() => {
                    if (ref && ref.current)
                        ref.current.setSelectionRange(caretPos, caretPos);
                });
            }
            else {
                setValue(props.value);
            }
        }
    }, [props.value]);
    (0, react_1.useEffect)(() => {
        const minimum = typeof min === 'number' ? min : Number(min);
        const maximum = typeof max === 'number' ? max : Number(max);
        if (isNumeric && isAbsoluteNumber && Number(value) < 0) {
            updateValue(Math.abs(Number(value)).toString());
        }
        else if (isNumeric && min !== undefined && Number(value) < minimum) {
            updateValue(Number(min).toString());
        }
        else if (isNumeric && max !== undefined && Number(value) > maximum) {
            updateValue(Number(max).toString());
        }
    }, [value]);
    const isPassword = isPasswordType(props.type);
    const isActive = !!(props.value || value);
    (0, react_1.useEffect)(() => {
        if (isPassword)
            setType(getPasswordType(hidden));
    }, [hidden]);
    const inputProps = Object.assign(Object.assign({}, otherProps), { min, max, isPassword, hasError, hasArrow, value, type, onChange: handleChange });
    return ((0, jsx_runtime_1.jsxs)(Input_styles_1.StyledContainer, { className, isFullWidth, children: [(0, jsx_runtime_1.jsxs)(Input_styles_1.StyledInputContainer, { children: [startIcon && (0, jsx_runtime_1.jsx)(Input_styles_1.StyledStartIconWrapper, { disabled: props.disabled, children: startIcon }), (0, jsx_runtime_1.jsxs)(Input_styles_1.StyledLabel, { isFullWidth, children: [(0, jsx_runtime_1.jsx)(Input_styles_1.StyledInput, Object.assign({ ref: (node) => {
                                    inputRef.current = node;
                                    if (typeof ref === 'function')
                                        ref(node);
                                    else if (ref)
                                        ref.current = node;
                                }, onKeyDown: handleKeyDown, startIcon: startIcon, clearable: clearable }, inputProps)), !props.disabled && label && (0, jsx_runtime_1.jsx)(Input_styles_1.StyledLabelText, { isActive, hasError, children: label })] }), !props.disabled && clearable && value && ((0, jsx_runtime_1.jsx)(Input_styles_1.StyledEndIconBtn, { disabled: props.disabled, onClick: handleClear, "aria-label": "clear", children: (0, jsx_runtime_1.jsx)(Error_1.IconError, { width: "24", height: "24" }) })), isPassword && (0, jsx_runtime_1.jsx)(PasswordToggle, { onClick: togglePassword, hidden }), hasArrow && isNumeric && ((0, jsx_runtime_1.jsx)(Arrows, { increment: increment, decrement: decrement, max: max, min: min, value: value, disabled: props.disabled }))] }), (helperText || errorMessage) && (0, jsx_runtime_1.jsx)(exports.Message, { isError: hasError, children: helperText || errorMessage })] }));
});
exports.Input.displayName = 'Input';
exports.default = exports.Input;
